import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getTrip, getMyRole } from '@/services/tripService'
import {
  listProposals,
  createProposal,
  castVote,
  retractVote,
  acceptProposal,
  rejectProposal,
  deleteProposal,
  type ActivityProposalWithVotes,
} from '@/services/activityProposalService'
import { ActivityForm } from '@/components/ActivityForm'
import type { NewActivityFormValues } from '@/utils/schemas'

const CATEGORY_COLOR: Record<string, string> = {
  transport: '#c97f4a',
  accommodation: '#6e9aa8',
  discovery: '#c9a15a',
  meal: '#9a8fb0',
}

export function ProposalsPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const roleQuery = useQuery({
    queryKey: ['trip-role', tripId],
    queryFn: () => getMyRole(tripId!),
    enabled: !!tripId,
  })

  const proposalsQuery = useQuery({
    queryKey: ['proposals', tripId],
    queryFn: () => listProposals(tripId!),
    enabled: !!tripId,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['proposals', tripId] })

  const createMutation = useMutation({
    mutationFn: (values: NewActivityFormValues) => createProposal({ tripId: tripId!, ...values }),
    onSuccess: async () => {
      await invalidate()
      setShowForm(false)
    },
  })

  const voteMutation = useMutation({
    mutationFn: ({ proposalId, hasVoted }: { proposalId: string; hasVoted: boolean }) =>
      hasVoted ? retractVote(proposalId) : castVote(proposalId),
    onSuccess: invalidate,
  })

  const acceptMutation = useMutation({
    mutationFn: (proposal: ActivityProposalWithVotes) => acceptProposal(proposal),
    onSuccess: invalidate,
  })

  const rejectMutation = useMutation({
    mutationFn: (proposalId: string) => rejectProposal(proposalId),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (proposalId: string) => deleteProposal(proposalId),
    onSuccess: async () => {
      await invalidate()
      setConfirmDeleteId(null)
    },
  })

  if (tripQuery.isLoading || proposalsQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('proposals.loading')}</p>
  }

  const trip = tripQuery.data
  const proposals = proposalsQuery.data ?? []
  const canDecide = roleQuery.data === 'owner' || roleQuery.data === 'companion'
  const pending = proposals.filter((p) => p.status === 'pending')
  const decided = proposals.filter((p) => p.status !== 'pending')

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('proposals.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('proposals.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        {pending.length === 0 && !showForm && (
          <p className="text-sm text-mist">{t('proposals.empty')}</p>
        )}

        {pending.map((proposal) => (
          <div
            key={proposal.id}
            className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3.5"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 font-sans text-[15px] font-medium text-ivory">
                {proposal.category && (
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ background: CATEGORY_COLOR[proposal.category] }}
                  />
                )}
                {proposal.title}
              </div>
              {proposal.location && (
                <div className="mt-1 text-sm text-mist">{proposal.location}</div>
              )}
              {proposal.note && <div className="mt-1 text-sm text-mist">{proposal.note}</div>}
              {confirmDeleteId === proposal.id ? (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-ivory">{t('proposals.confirmDelete')}</span>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded border border-line-strong px-3 py-1 font-sans text-xs text-ivory"
                  >
                    {t('proposals.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(proposal.id)}
                    disabled={deleteMutation.isPending}
                    className="rounded border border-brass-warm px-3 py-1 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
                  >
                    {t('proposals.confirmDeleteButton')}
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(proposal.id)}
                    className="font-sans text-xs text-mist hover:text-brass-warm"
                  >
                    {t('proposals.delete')}
                  </button>
                  {canDecide && (
                    <>
                      <button
                        type="button"
                        onClick={() => acceptMutation.mutate(proposal)}
                        disabled={acceptMutation.isPending}
                        className="font-sans text-xs text-brass hover:text-brass-warm"
                      >
                        {t('proposals.accept')}
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectMutation.mutate(proposal.id)}
                        disabled={rejectMutation.isPending}
                        className="font-sans text-xs text-mist hover:text-brass-warm"
                      >
                        {t('proposals.reject')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() =>
                voteMutation.mutate({ proposalId: proposal.id, hasVoted: proposal.hasVoted })
              }
              className={
                'flex shrink-0 flex-col items-center rounded border px-3 py-2 font-sans text-xs ' +
                (proposal.hasVoted
                  ? 'border-brass text-brass'
                  : 'border-line-strong text-ivory hover:border-brass hover:text-brass')
              }
            >
              <span>{proposal.hasVoted ? t('proposals.voted') : t('proposals.vote')}</span>
              <span className="font-mono text-sm">{proposal.voteCount}</span>
            </button>
          </div>
        ))}

        {showForm ? (
          <ActivityForm
            isPending={createMutation.isPending}
            isError={createMutation.isError}
            submitLabel={t('proposals.submit')}
            onCancel={() => setShowForm(false)}
            onSubmit={(values) => createMutation.mutate(values)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-4 rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night"
          >
            {t('proposals.propose')}
          </button>
        )}

        {decided.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 font-sans text-sm font-medium text-mist">
              {t('proposals.decided')}
            </h2>
            {decided.map((proposal) => (
              <div
                key={proposal.id}
                className="mb-2 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3 opacity-60"
              >
                <span className="font-sans text-sm text-ivory">{proposal.title}</span>
                <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                  {t(`proposals.status.${proposal.status}`)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
